import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { t } from "i18next";

import { Button, TextField } from "@components/base";

const schema = object({
  email: string()
    .email(t("validation.email"))
    .max(255)
    .required(t("validation.required")),
}).required();

export type PasswordRecoveryFormValues = {
  email: string;
};

interface PasswordRecoveryForm {
  onSubmit: (values: PasswordRecoveryFormValues) => void;
  submitting: boolean;
  serverError?: string;
}

const PasswordRecoveryForm: FC<PasswordRecoveryForm> = ({
  onSubmit,
  submitting,
  serverError,
}) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<PasswordRecoveryFormValues>({
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(schema),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="column" rowGap={3}>
        <Controller
          control={control}
          name="email"
          render={({
            field: { onBlur, onChange, name },
            fieldState: { error },
          }): React.ReactElement => (
            <TextField
              {...{ onBlur, onChange, name }}
              error={Boolean(error) || Boolean(serverError)}
              variant="outlined"
              label={t("settings.emailAddress")}
              helperText={
                error?.message || (serverError && t(`validation.${serverError}`))
              }
              placeholder={t("settings.emailAddress")}
              type="email"
            />
          )}
        />

        <Button
          isLoading={submitting}
          size="medium"
          variant="contained"
          type="submit"
        >
          {t("auth.sendResetLink")}
        </Button>
      </Stack>
    </form>
  );
};

export default PasswordRecoveryForm;
